import React, {
  useEffect,
  useCallback,
  useMemo,
  useState,
  Fragment,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useIntl } from 'react-intl';
import classnames from 'classnames';
import messages from './messages';
import ProductCard from '../../../components/pdpV2/ProductCard';
import {
  setSelectedBedFrameAOVSKUs,
  setMattressAndFrameOption,
  setSelectedFirstProductVariant,
} from '../../../slices/pdp';
import styles from './BedFrameAOV.module.scss';
import ExtendWarrantyPlaceholder from '../../../components/pdpV2/ExtendWarrantyPlaceholder';
import {
  MATTRESS_AND_FRAME_OPTION,
  MATTRESS_ONLY_OPTION,
  ProductCardType,
} from '../constants';
import {
  IS_OUT_OF_STOCK,
  PURPLE_PREMIUM_SMART_BASE,
  PURPLE_PREMIUM_PLUS_SMART_BASE,
  DEFAULT_IN_HOME_SETUP_PRICE_CENT,
} from '../../../utils/common/constants';
import useDrupalSettings from '../../../hooks/useDrupalSettings';
import { getDiscountPercent, priceToString } from '../../../utils';
import VariantSelection from '../VariantSelection';
import AlternativeProductWrapper from '../../../components/AlternativeProductWrapper/AlternativeProductWrapper';
import { findVariantByAttributeValues } from '../../../utils/variants';
import slaConfig from '../../../components/Sla/slaConfig.json';
import { getCookie } from '../../../utils/common';

const BedFrameAOV = () => {
  const dispatch = useDispatch();
  const { aovs, product: productFromDrupalSettings } = useDrupalSettings();
  const { formatMessage, formatNumber, locale } = useIntl();

  const [isOptionsExpanded, setIsOptionsExpanded] = useState(false);

  const {
    selectedSKU,
    selectedBedFrameAOVSKUs,
    mattressAndFrameOption,
    detectedWarrantyPlansForPlaceholders,
  } = useSelector((state) => state.pdp);

  const bedFrameAOVForSelectedSKU = aovs?.find(
    ({ targetSku }) => targetSku === selectedSKU,
  )?.frames;

  const [altProductMap, setAltProductMap] = useState({});
  const arrayOfVariants = bedFrameAOVForSelectedSKU?.options
    .map(({ variants }) => variants)
    .flat();

  const initialAltProductMap = useMemo(
    () =>
      Object.fromEntries(
        bedFrameAOVForSelectedSKU?.options.map((option) => [
          option.productId,
          option.alternativeProduct,
        ]) ?? [],
      ),
    [bedFrameAOVForSelectedSKU],
  );

  const hasAltVariantSelected =
    bedFrameAOVForSelectedSKU?.options
      .find((option) => option?.alternativeProduct !== undefined)
      ?.alternativeProduct?.product?.variants.find((variant) =>
        selectedBedFrameAOVSKUs.includes(variant.sku),
      ) !== undefined;

  const hasAltProductSelected =
    !!bedFrameAOVForSelectedSKU?.options.find(({ variants }) =>
      variants.find(({ sku }) => selectedBedFrameAOVSKUs.includes(sku)),
    ) || hasAltVariantSelected;

  useEffect(
    () => {
      if (
        (!selectedBedFrameAOVSKUs.length || !hasAltProductSelected) &&
        mattressAndFrameOption === MATTRESS_AND_FRAME_OPTION &&
        bedFrameAOVForSelectedSKU
      ) {
        dispatch(setSelectedBedFrameAOVSKUs([]));
        setAltProductMap(initialAltProductMap);
      }
    }, // Beware to add variables to dependency array manually
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      dispatch,
      initialAltProductMap,
      selectedBedFrameAOVSKUs.length,
      hasAltProductSelected,
      mattressAndFrameOption,
      hasAltVariantSelected,
      setAltProductMap,
    ],
  );

  const onChangeProductCard = (item) => {
    if (!selectedBedFrameAOVSKUs.includes(item)) {
      const newValue = arrayOfVariants.some(({ sku }) => sku === item)
        ? [item]
        : [...selectedBedFrameAOVSKUs, item];
      dispatch(setSelectedBedFrameAOVSKUs(newValue));

      dispatch(
        setMattressAndFrameOption({
          mattressAndFrameOption: MATTRESS_AND_FRAME_OPTION,
        }),
      );
    } else {
      dispatch(
        setMattressAndFrameOption({
          mattressAndFrameOption: MATTRESS_ONLY_OPTION,
        }),
      );
      dispatch(setSelectedBedFrameAOVSKUs([]));
    }
  };

  const onChangeProductVariant = ({ sku, productKey }) => {
    const selectedOption = bedFrameAOVForSelectedSKU?.options?.find(
      (option) => option.productId === productKey,
    );

    const newBedframeAOVVariantSelected = selectedOption?.variants?.find(
      (variant) => variant.sku === sku,
    );

    const matchingAltProductVariant = findVariantByAttributeValues({
      variants: altProductMap[productKey]?.product?.variants,
      attributeValues: newBedframeAOVVariantSelected?.attributes,
      returnPartialMatchingIfFullMatchIsNotFound: true,
    });

    const matchingAltProductVariantIsOutOfStock =
      !!matchingAltProductVariant?.isOutOfStock;

    const matchingAltProductVariantSkuArray =
      hasAltVariantSelected &&
      matchingAltProductVariant &&
      !matchingAltProductVariantIsOutOfStock
        ? [matchingAltProductVariant.sku]
        : [];
    dispatch(
      setSelectedBedFrameAOVSKUs([sku, ...matchingAltProductVariantSkuArray]),
    );
  };

  const addRemoveAltProductCallback = useCallback(
    (productId) => {
      let arrayOfSkus = [];

      const selectedOption = bedFrameAOVForSelectedSKU?.options?.find(
        (option) => option.productId === productId,
      );

      if (!hasAltVariantSelected) {
        const bedframeAOVVariantSelected = selectedOption?.variants?.find(
          (variant) => selectedBedFrameAOVSKUs.includes(variant.sku),
        );

        const matchingAltProductVariant = findVariantByAttributeValues({
          variants: altProductMap[productId]?.product?.variants,
          attributeValues:
            bedframeAOVVariantSelected?.attributes ??
            setSelectedFirstProductVariant?.attributes,
          returnPartialMatchingIfFullMatchIsNotFound: true,
        });

        const altProductMasterVariant = altProductMap[
          productId
        ]?.product?.variants.find((variant) => variant.masterVariant);

        arrayOfSkus.push(
          matchingAltProductVariant?.sku ||
            altProductMasterVariant?.sku ||
            altProductMap[productId]?.product?.variants?.[0]?.sku,
        );

        arrayOfSkus.push(bedframeAOVVariantSelected.sku);
      } else {
        const altProductVariantSelected = altProductMap[
          productId
        ]?.product?.variants.find((variant) =>
          selectedBedFrameAOVSKUs.includes(variant.sku),
        );

        arrayOfSkus = altProductVariantSelected
          ? [...selectedBedFrameAOVSKUs]?.filter(
              (selectedBedFrameAOVSKU) =>
                selectedBedFrameAOVSKU !== altProductVariantSelected.sku,
            )
          : [...selectedBedFrameAOVSKUs];
      }
      dispatch(setSelectedBedFrameAOVSKUs(arrayOfSkus));
    },
    [
      bedFrameAOVForSelectedSKU?.options,
      hasAltVariantSelected,
      dispatch,
      altProductMap,
      selectedBedFrameAOVSKUs,
    ],
  );

  const containerClass =
    bedFrameAOVForSelectedSKU?.options?.length > 0
      ? 'bedframe-options-container'
      : '';

  const defaultBedFrameAOVOption = bedFrameAOVForSelectedSKU?.options?.[0];

  const alternativeBedFrameAOVOptions =
    bedFrameAOVForSelectedSKU?.options?.filter(
      (option) => option.productId !== defaultBedFrameAOVOption?.productId,
    );

  const renderBedFrameAOVOption = (frameAOVOption) => {
    const variantSelected = frameAOVOption.variants?.find((variant) =>
      selectedBedFrameAOVSKUs.includes(variant.sku),
    );
    const isProductVariantSelected = !!variantSelected;

    const isProductAltVariantSelected = !!bedFrameAOVForSelectedSKU?.options
      .find((option) => option.productId === frameAOVOption.productId)
      ?.alternativeProduct?.product.variants.find((altProductVariant) =>
        selectedBedFrameAOVSKUs.includes(altProductVariant.sku),
      );

    const currentVariantForBedFrameAOVOption =
      variantSelected ?? frameAOVOption.variants?.[0];
    const hasVariants = frameAOVOption.variants?.length > 1;

    const altProductVariants =
      frameAOVOption?.alternativeProduct?.product?.variants;

    const foundAltProductVariant = findVariantByAttributeValues({
      variants: altProductVariants,
      attributeValues: currentVariantForBedFrameAOVOption?.attributes,
      returnPartialMatchingIfFullMatchIsNotFound: true,
    });

    const IS_OUT_OF_STOCK_TRUE_KEY = 'true';

    const isFoundAltProductVariantOutOfStock =
      foundAltProductVariant?.attributes?.find(
        (attribute) => attribute.name === IS_OUT_OF_STOCK,
      )?.key === IS_OUT_OF_STOCK_TRUE_KEY;

    const hasAlternativeProduct =
      frameAOVOption?.alternativeProduct !== undefined &&
      altProductVariants?.length > 0 &&
      !!foundAltProductVariant &&
      !isFoundAltProductVariantOutOfStock;

    const altProductHasVariants = altProductVariants?.length > 1;

    const currentVariantForAltProduct =
      altProductVariants?.find((variant) =>
        selectedBedFrameAOVSKUs.includes(variant.sku),
      ) ||
      foundAltProductVariant ||
      altProductVariants?.find((variant) => variant.masterVariant);

    const altVariantToShowInProductSwitch =
      altProductVariants?.find((variant) =>
        selectedBedFrameAOVSKUs.includes(variant.sku),
      ) ||
      (isProductVariantSelected &&
        findVariantByAttributeValues({
          variants: altProductVariants,
          attributeValues: currentVariantForBedFrameAOVOption?.attributes,
          returnPartialMatchingIfFullMatchIsNotFound: true,
        })) ||
      altProductVariants?.[0];

    const isAltProductVariantSelected = !!selectedBedFrameAOVSKUs?.find(
      (currentSelectedSku) =>
        currentSelectedSku === altVariantToShowInProductSwitch?.sku,
    );

    if (currentVariantForBedFrameAOVOption) {
      const { price, discount } = currentVariantForBedFrameAOVOption;
      const discountAmount = frameAOVOption?.isAbsoluteDiscount
        ? priceToString({
            price: discount,
            maximumFractionDigits: 0,
            formatNumber,
            formatMessage,
          })
        : `${getDiscountPercent(price?.centAmount, discount?.centAmount)}%`;
      const discountMessage =
        discount?.centAmount > 0
          ? formatMessage(messages.discountBadge, {
              discount: discountAmount,
            })
          : '';

      const inHomeSetupPricing = priceToString({
        price: {
          centAmount:
            getCookie(
              process.env.CUSTOM_IN_HOME_SETUP_PRICING_AB_TEST_COOKIE_NAME,
            )?.value ?? DEFAULT_IN_HOME_SETUP_PRICE_CENT,
          fractionDigits: 2,
          currencyCode: 'USD',
        },
        maximumFractionDigits: 0,
        formatNumber,
        formatMessage,
      });

      const whiteGloveMessage =
        getCookie(
          process.env.OVERWRITE_PAID_IN_HOME_SHIPPING_AB_TEST_COOKIE_NAME,
        )?.value &&
        [PURPLE_PREMIUM_SMART_BASE, PURPLE_PREMIUM_PLUS_SMART_BASE].includes(
          frameAOVOption.productKey,
        ) &&
        !!slaConfig.mattress.find(
          (product) => product.key === productFromDrupalSettings.key,
        )
          ? formatMessage(messages.freeInHomeSetupWithDiscount, {
              pricing: inHomeSetupPricing,
            })
          : '';

      return (
        <>
          <div
            className={classnames(styles['frame-option'], {
              [styles['is-show-variants']]:
                isProductVariantSelected && hasVariants,
            })}
          >
            <ProductCard
              title={frameAOVOption.productName[locale]}
              id={currentVariantForBedFrameAOVOption.sku}
              name={currentVariantForBedFrameAOVOption.sku}
              description={frameAOVOption.description[locale]}
              price={
                isAltProductVariantSelected
                  ? {
                      ...(currentVariantForAltProduct?.price ?? {}),
                      centAmount:
                        currentVariantForAltProduct?.price.centAmount +
                        currentVariantForBedFrameAOVOption?.price.centAmount,
                    }
                  : currentVariantForBedFrameAOVOption.price
              }
              discount={
                isAltProductVariantSelected
                  ? {
                      ...(currentVariantForAltProduct?.discount ?? {}),
                      centAmount:
                        currentVariantForAltProduct?.discount.centAmount +
                        currentVariantForBedFrameAOVOption?.discount.centAmount,
                    }
                  : currentVariantForBedFrameAOVOption.discount
              }
              imageURL={
                isAltProductVariantSelected
                  ? frameAOVOption?.alternativeProduct?.images?.find(
                      (image) => image.sku === currentVariantForAltProduct.sku,
                    )?.imageUrl
                  : currentVariantForBedFrameAOVOption.image
              }
              isSelected={
                isProductVariantSelected || isProductAltVariantSelected
              }
              onChange={onChangeProductCard}
              badgeStyle={ProductCardType.ALTERNATIVE}
              badge={discountMessage}
              whiteGloveMessage={whiteGloveMessage}
            />
          </div>

          {((isProductVariantSelected && hasVariants) ||
            (isProductAltVariantSelected && altProductHasVariants)) && (
            <>
              {hasAlternativeProduct && (
                <div
                  className={styles['alternative-product-selector-container']}
                >
                  <AlternativeProductWrapper
                    id={frameAOVOption.productId}
                    isChecked={isAltProductVariantSelected}
                    onChecked={addRemoveAltProductCallback}
                    alternativeProduct={frameAOVOption?.alternativeProduct}
                    altVariantToShow={altVariantToShowInProductSwitch}
                  />
                </div>
              )}
              <div className={styles['variant-selector-container']}>
                <VariantSelection
                  variants={frameAOVOption.variants}
                  defaultSelectedSKU={currentVariantForBedFrameAOVOption?.sku}
                  onSelectVariant={({ sku }) => {
                    onChangeProductVariant({
                      sku,
                      productKey: frameAOVOption.productId,
                    });
                  }}
                  sortColors={false}
                />
              </div>
            </>
          )}
        </>
      );
    }
    return null;
  };

  return bedFrameAOVForSelectedSKU?.options?.length > 0 ? (
    <div className={styles[`${containerClass}`]}>
      {defaultBedFrameAOVOption &&
        renderBedFrameAOVOption(defaultBedFrameAOVOption)}

      {alternativeBedFrameAOVOptions?.length &&
      alternativeBedFrameAOVOptions?.[0]?.variants?.length ? (
        <>
          <button
            type="button"
            className={classnames(styles['options-expand-btn'], {
              [styles['is-expanded']]: isOptionsExpanded,
            })}
            onClick={() => setIsOptionsExpanded((prev) => !prev)}
          >
            <span>{formatMessage(messages.expand)}</span>
            <div className={styles['options-expand-btn-icon']} />
          </button>

          {isOptionsExpanded &&
            alternativeBedFrameAOVOptions?.map((option) => (
              <Fragment key={`BedFrameAOV_${option.productId}`}>
                {renderBedFrameAOVOption(option)}
              </Fragment>
            ))}
        </>
      ) : null}

      {selectedBedFrameAOVSKUs.length > 0 && (
        <ExtendWarrantyPlaceholder
          placeholderId="extend-offer-frame"
          productType="bed_frames"
          detectedWarrantyPlansForPlaceholders={
            detectedWarrantyPlansForPlaceholders
          }
          selectedProduct={selectedBedFrameAOVSKUs[0]}
        />
      )}
    </div>
  ) : null;
};

export default BedFrameAOV;
